import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WHEEL_IMG, FRAMEWORK, FrameworkKey } from '../data/site';
interface Hotspot {
  key: FrameworkKey;
  label: string;
  /** Hotspot button geometry, percentage-based over the wheel image. */
  style: React.CSSProperties;
  /**
   * Anchor point (in % of container) the callout points at, and the side the
   * callout opens toward. This lets each callout "hug" its own segment.
   */
  callout: {
    /** % from left of the segment anchor */
    x: number;
    /** % from top of the segment anchor */
    y: number;
    /** Which way the callout body sits relative to the anchor. */
    placement: 'top' | 'bottom' | 'left' | 'right';
  };
}
// Positioned over the supplied wheel image (percentage based, responsive).
const HOTSPOTS: Hotspot[] = [{
  key: 'inputs',
  label: 'Inputs',
  style: {
    top: '20%',
    left: '38%',
    width: '24%',
    height: '20%'
  },
  callout: {
    x: 50,
    y: 19,
    placement: 'top'
  }
}, {
  key: 'interpretation',
  label: 'Interpretation',
  style: {
    top: '36%',
    left: '60%',
    width: '24%',
    height: '22%'
  },
  callout: {
    x: 80,
    y: 46,
    placement: 'right'
  }
}, {
  key: 'decisions',
  label: 'Decisions',
  style: {
    top: '60%',
    left: '52%',
    width: '24%',
    height: '22%'
  },
  callout: {
    x: 66,
    y: 80,
    placement: 'bottom'
  }
}, {
  key: 'feedback',
  label: 'Feedback',
  style: {
    top: '60%',
    left: '24%',
    width: '24%',
    height: '22%'
  },
  callout: {
    x: 34,
    y: 80,
    placement: 'bottom'
  }
}, {
  key: 'improvements',
  label: 'Improvements',
  style: {
    top: '36%',
    left: '16%',
    width: '24%',
    height: '22%'
  },
  callout: {
    x: 20,
    y: 46,
    placement: 'left'
  }
}, {
  key: 'operating',
  label: 'Operating Layer',
  style: {
    top: '6%',
    left: '24%',
    width: '52%',
    height: '10%'
  },
  callout: {
    x: 50,
    y: 5,
    placement: 'top'
  }
}];
/**
 * Geometry for a callout given its placement. Returns the wrapper positioning
 * (anchored at the segment point) plus the translate that flips the body to the
 * correct side and the caret styles pointing back at the segment.
 */
function calloutGeometry(placement: Hotspot['callout']['placement']) {
  switch (placement) {
    case 'top':
      // Body sits above the anchor.
      return {
        translate: 'translate(-50%, -100%)',
        margin: {
          marginTop: -10
        },
        caret: 'left-1/2 top-full -translate-x-1/2 -mt-px border-x-[6px] border-t-[7px] border-x-transparent'
      };
    case 'bottom':
      return {
        translate: 'translate(-50%, 0%)',
        margin: {
          marginTop: 10
        },
        caret: 'left-1/2 bottom-full -translate-x-1/2 -mb-px border-x-[6px] border-b-[7px] border-x-transparent rotate-180'
      };
    case 'left':
      return {
        translate: 'translate(-100%, -50%)',
        margin: {
          marginLeft: -10
        },
        caret: 'top-1/2 left-full -translate-y-1/2 -ml-px border-y-[6px] border-l-[7px] border-y-transparent'
      };
    case 'right':
    default:
      return {
        translate: 'translate(0%, -50%)',
        margin: {
          marginLeft: 10
        },
        caret: 'top-1/2 right-full -translate-y-1/2 -mr-px border-y-[6px] border-r-[7px] border-y-transparent'
      };
  }
}
export function FrameworkWheel() {
  const [active, setActive] = useState<FrameworkKey | null>(null);
  const activeSpot = HOTSPOTS.find(s => s.key === active) || null;
  const activeComp = FRAMEWORK.find(c => c.key === active) || null;
  return <div className="relative mx-auto w-full max-w-[520px]" data-id="element-52">
      <div className="relative" data-id="element-53">
        <motion.img src={WHEEL_IMG} alt="The Mine Geology Systems framework wheel: Inputs, Interpretation, Decisions, Feedback and Improvements inside an Operating Layer of People, Processes, Standards and Consistency." className="w-full select-none" initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} draggable={false} data-id="element-54" />

        {HOTSPOTS.map(spot => {
        const comp = FRAMEWORK.find(c => c.key === spot.key)!;
        const isActive = active === spot.key;
        return <button key={spot.key} type="button" aria-label={`${spot.label}: ${comp.short}`} className="absolute rounded-2xl transition-all duration-200" style={{
          ...spot.style,
          boxShadow: isActive ? `0 0 0 3px ${comp.color}` : 'none',
          backgroundColor: isActive ? `${comp.color}1f` : 'transparent'
        }} onMouseEnter={() => setActive(spot.key)} onMouseLeave={() => setActive(null)} onFocus={() => setActive(spot.key)} onBlur={() => setActive(null)} onClick={() => setActive(isActive ? null : spot.key)} data-id="element-55" />;
      })}

        {/* Anchored, segment-hugging callout */}
        <AnimatePresence data-id="element-56">
          {activeSpot && activeComp && <CalloutCard key={activeSpot.key} spot={activeSpot} color={activeComp.color} name={activeComp.name} short={activeComp.short} data-id="element-57" />}
        </AnimatePresence>
      </div>

      {/* Minimal persistent caption — quiet helper / mobile fallback. */}
      <p className="mt-4 text-center text-xs text-warmgray" aria-hidden data-id="element-58">
        {activeComp ? <span className="inline-flex items-center gap-1.5" data-id="element-59">
            <span className="h-2 w-2 rounded-sm" style={{
          backgroundColor: activeComp.color
        }} data-id="element-60" />
            <span className="font-semibold text-charcoal" data-id="element-61">
              {activeComp.name}
            </span>
            <span className="text-warmgray" data-id="element-62">— {activeComp.short}</span>
          </span> : 'Hover, focus or tap a segment of the wheel to explore each part of the framework.'}
      </p>
    </div>;
}
interface CalloutCardProps {
  spot: Hotspot;
  color: string;
  name: string;
  short: string;
}
function CalloutCard({
  spot,
  color,
  name,
  short
}: CalloutCardProps) {
  const geo = calloutGeometry(spot.callout.placement);
  const enterOffset = spot.callout.placement === 'top' ? {
    y: 6
  } : spot.callout.placement === 'bottom' ? {
    y: -6
  } : spot.callout.placement === 'left' ? {
    x: 6
  } : {
    x: -6
  };
  return <motion.div className="pointer-events-none absolute z-20" style={{
    left: `${spot.callout.x}%`,
    top: `${spot.callout.y}%`,
    transform: geo.translate,
    ...geo.margin
  }} initial={{
    opacity: 0,
    scale: 0.94,
    ...enterOffset
  }} animate={{
    opacity: 1,
    scale: 1,
    x: 0,
    y: 0
  }} exit={{
    opacity: 0,
    scale: 0.94,
    ...enterOffset
  }} transition={{
    duration: 0.18,
    ease: 'easeOut'
  }} aria-hidden data-id="element-63">
      <div className="relative w-[min(220px,68vw)] rounded-lg border border-charcoal/10 bg-cream/95 p-3 shadow-[0_8px_24px_-8px_rgba(31,30,28,0.35)] backdrop-blur-sm" style={{
      borderTopColor: color,
      borderTopWidth: 2
    }} data-id="element-64">
        <div className="flex items-center gap-1.5" data-id="element-65">
          <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{
          backgroundColor: color
        }} data-id="element-66" />
          <span className="text-[13px] font-bold leading-none text-charcoal" data-id="element-67">
            {name}
          </span>
        </div>
        <p className="mt-1.5 text-xs leading-snug text-warmgray" data-id="element-68">{short}</p>

        {/* Caret pointing back toward the segment */}
        <span className={`absolute h-0 w-0 ${geo.caret}`} style={spot.callout.placement === 'top' ? {
        borderTopColor: '#fbf8f1'
      } : spot.callout.placement === 'bottom' ? {
        borderBottomColor: '#fbf8f1'
      } : spot.callout.placement === 'left' ? {
        borderLeftColor: '#fbf8f1'
      } : {
        borderRightColor: '#fbf8f1'
      }} aria-hidden data-id="element-69" />
      </div>
    </motion.div>;
}